import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import axios from 'axios';
import { getUserEmail } from '../../../utils/authUtils';
import { getLogoUrlSync, getLogoUrl } from '../../../utils/logoUtils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ApplicationFormPage1 from './ApplicationFormPage1'; // Added import for Page 1
import { fetchApplicationDetails } from '../../../utils/apiUtils'; // Assuming an API utility

// Define ApplicationFormData type
interface ApplicationFormData {
  email: string;
  fullName: string;
  dateOfBirth: string;
  gender: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: string;
  vehicleVIN: string;
  fuelType: string;
  conversionType: string;
  kitType: string;
  cylinderSize: string;
  drivingLicense: File | null;
  vehicleRegistration: File | null;
  vehicleInsurance: File | null;
  idProof: File | null; // Added ID Proof
  termsAgreed: boolean;
}

// API URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Initial form data state (moved outside component for stable reference)
const initialFormDataState: ApplicationFormData = {
  email: '',
  fullName: '',
  dateOfBirth: '',
  gender: '',
  phoneNumber: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  vehicleMake: '',
  vehicleModel: '',
  vehicleYear: '',
  vehicleVIN: '',
  fuelType: '',
  conversionType: '',
  kitType: '',
  cylinderSize: '',
  drivingLicense: null,
  vehicleRegistration: null,
  vehicleInsurance: null,
  idProof: null, // Added ID Proof
  termsAgreed: false
};

// Styled component for the floating back button
const FloatingBackButton = styled(motion.button)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  background: linear-gradient(135deg, #4a6cf7 0%, #6a82fb 100%);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 20px;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 1000;
  transition: all 0.3s ease;
  min-height: 48px;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: linear-gradient(135deg, #3a56d4 0%, #5a72e9 100%);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.25);
    transform: translateY(-2px);
  }

  svg {
    font-size: 20px;
  }
  
  @media (max-width: 480px) {
    bottom: 20px;
    right: 20px;
    padding: 10px 16px;
    font-size: 14px;
  }
`;

// Types and interfaces
interface ApplicationSummary {
  applicationId: string;
  status?: string;
  createdAt: string;
  updatedAt: string;
  isComplete?: boolean;
  isSubmitted?: boolean;
}

// Styled components for a consistent UI
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  background: linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%);
  padding: 20px;
  font-family: 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  
  @media (max-width: 768px) {
    padding: 12px;
    align-items: flex-start;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
  }
`;

const Card = styled(motion.div)`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(76, 110, 245, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 720px;
  margin: 20px auto;
  border: 1px solid rgba(226, 232, 240, 0.8);
  
  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 12px;
    margin: 10px auto;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 10px;
    margin: 5px auto;
  }
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 30px;
  position: relative;
`;

const Logo = styled.img`
  max-width: 339px;
  max-height: 75px;
  height: auto;
  object-fit: contain;
  margin-bottom: 16px;
  
  @media (max-width: 480px) {
    max-width: 260px;
    max-height: 55px;
  }
  
  @media (max-width: 375px) {
    max-width: 220px;
    max-height: 48px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #2d3748;
  text-align: center;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  letter-spacing: -0.5px;
`;

const ApplicationIdContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background-color: #f0f4ff;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid #e0e7ff;
  box-shadow: 0 2px 8px rgba(76, 110, 245, 0.1);
  margin: 0 auto 16px auto;
  max-width: fit-content;
`;

const ApplicationIdLabel = styled.span`
  font-size: 13px;
  color: #4b5563;
  font-weight: 500;
`;

const ApplicationIdValue = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #4c6ef5;
  letter-spacing: 0.5px;
  background-color: #ffffff;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #e0e7ff;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #4a5568;
  text-align: center;
  margin-bottom: 30px;
  line-height: 1.6;
  max-width: 80%;
  margin-left: auto;
  margin-right: auto;
  
  span {
    color: #4c6ef5;
    font-weight: 600;
  }
`;


const FormGroup = styled.div`
  margin-bottom: 20px;
  transition: all 0.3s ease;
  
  &:hover label {
    color: #4c6ef5;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  letter-spacing: 0.2px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  font-size: 16px; /* 16px prevents iOS zoom */
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background-color: #fcfcff;
  outline: none;
  transition: all 0.3s ease;
  color: #2d3748;
  -webkit-appearance: none;
  
  &:hover {
    border-color: #cbd5e0;
    background-color: #ffffff;
  }
  
  &:focus {
    border-color: #4c6ef5;
    box-shadow: 0 0 0 4px rgba(76, 110, 245, 0.15);
    background-color: #ffffff;
  }
  
  &::placeholder {
    color: #a0aec0;
  }
  
  @media (max-width: 480px) {
    padding: 14px 12px;
  }
`;

const Button = styled.button`
  display: block;
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  box-shadow: 0 4px 12px rgba(76, 110, 245, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(76, 110, 245, 0.3);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(76, 110, 245, 0.2);
  }
  
  &:disabled {
    background: #e2e8f0;
    color: #a0aec0;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ExitButton = styled(motion.button)`
  position: fixed;
  top: 24px;
  right: 24px;
  background-color: white;
  color: #4a5568;
  border: none;
  border-radius: 50px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  z-index: 100;
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  &:hover {
    background-color: #f8f9fa;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  }
`;

const PreviousApplicationsButton = styled(motion.button)`
  position: fixed;
  top: 24px; /* Position to the left of the New Application button */
  background-color: #4a5568;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(74, 85, 104, 0.3);
  transition: all 0.2s ease;
  z-index: 100;
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  &:hover {
    background-color: #2d3748;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(74, 85, 104, 0.4);
  }
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  padding: 30px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h2 {
    margin: 0;
    font-size: 20px;
    color: #2d3748;
  }
  
  button {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #a0aec0;
    
    &:hover {
      color: #4a5568;
    }
  }
`;

const ApplicationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ApplicationItem = styled(motion.div)`
  padding: 16px;
  border-radius: 8px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #edf2f7;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const ApplicationInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .application-id {
    font-weight: 600;
    color: #2d3748;
    font-size: 16px;
  }
  
  .application-date {
    color: #718096;
    font-size: 14px;
  }
`;

const ApplicationStatus = styled.div<{ status?: string }>`
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  
  .status {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    background-color: ${props => {
      if (props.status === 'submitted') return '#ebf8ff';
      if (props.status === 'approved') return '#f0fff4';
      if (props.status === 'rejected') return '#fff5f5';
      return '#f7fafc'; // default (draft/incomplete)
    }};
    color: ${props => {
      if (props.status === 'submitted') return '#3182ce';
      if (props.status === 'approved') return '#38a169';
      if (props.status === 'rejected') return '#e53e3e';
      return '#718096'; // default (draft/incomplete)
    }};
  }
  
  .completion {
    font-size: 12px;
    color: #718096;
  }
`;

// Extend Window interface to include our custom properties
declare global {
  interface Window {
    saveTimeout?: NodeJS.Timeout;
  }
}

// @ts-ignore - Ignoring JSX intrinsic elements errors
// Function to generate a random 11-character alphanumeric ID
const generateApplicationId = (): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 11; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

// Function to save application data to the database
const saveApplicationToDatabase = async (applicationId: string, data: ApplicationFormData) => {
  try {
    // Save to localStorage as a backup/cache
    localStorage.setItem(`form_data_${applicationId}`, JSON.stringify(data));
    
    // Configure axios to send credentials (cookies)
    axios.defaults.withCredentials = true;
    
    // Make API call to save application data
    const response = await axios.put(`${API_URL}/api/applications/${applicationId}`, {
      formData: data
    });
    
    if (response.data.success) {
      console.log('Application data saved successfully');
    } else {
      console.error('Error saving application data:', response.data.message);
    }
  } catch (error) {
    console.error('Error saving application data:', error);
    localStorage.setItem(`form_data_${applicationId}`, JSON.stringify(data));
  }
};

// Function to create a new application via API
const createNewApplication = async (email: string) => {
  try {
    // Configure axios to send credentials (cookies)
    axios.defaults.withCredentials = true;
    
    // Create a new minimal application shell for saving progress
    const response = await axios.post(`${API_URL}/api/applications`, {
      email: email // Backend /api/applications/create expects 'email' in the body
    });
    
    if (response.data.success) {
      const { application } = response.data;
      // Store the application ID in session storage
      sessionStorage.setItem('currentApplicationId', application.applicationId);
      return application;
    } else {
      console.error('Error creating new application:', response.data.message);
      return null;
    }
  } catch (error) {
    console.error('Error creating new application:', error);
    return null;
  }
};

const ApplicationForm: React.FC = () => {
  const location = useLocation();
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [isViewMode, setIsViewMode] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Initialize isLoading to true
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const currentApplicationIdFromState = location.state?.applicationId as string;
    const currentApplicationIdFromSession = sessionStorage.getItem('currentApplicationId');
    let currentApplicationId = currentApplicationIdFromState || currentApplicationIdFromSession;
    
    // Check for view mode flag in session storage
    const viewModeFromSession = sessionStorage.getItem('viewMode');
    if (viewModeFromSession === 'true') {
      setIsViewMode(true);
    }

    if (currentApplicationIdFromState && currentApplicationIdFromState !== currentApplicationIdFromSession) {
      // Prioritize state if different, and update session
      sessionStorage.setItem('currentApplicationId', currentApplicationIdFromState);
      currentApplicationId = currentApplicationIdFromState;
    }

    if (currentApplicationId) {
      setApplicationId(currentApplicationId);
      // Ensure session storage is set if we derived the ID from state
      if (!currentApplicationIdFromSession || currentApplicationIdFromSession !== currentApplicationId) {
        sessionStorage.setItem('currentApplicationId', currentApplicationId);
      }
    } else {
      console.warn('No application ID found in location state or session storage.');
      // Potentially, this could be a new application flow if ApplicationFormPage1 handles ID generation.
      // For now, we'll proceed, and ApplicationFormPage1 might try to generate/fetch one.
      // If an ID is strictly required *before* rendering Page 1, this needs more robust handling.
      setIsLoading(false); // Not loading if no ID to fetch status for
      return;
    }

    const getApplicationStatus = async () => {
      if (!currentApplicationId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        // Using the imported fetchApplicationDetails function
        const appData = await fetchApplicationDetails(currentApplicationId);

        const viewModeFromSession = sessionStorage.getItem('viewMode');
        
        if (viewModeFromSession === 'true') {
          // If view mode is explicitly set in session storage, always use that
          setIsViewMode(true);
        } else if (appData && appData.status) {
          // Otherwise determine view mode based on application status
          const nonEditableStatuses = ['Submitted', 'Approved', 'Rejected', 'Under Review', 'payment_complete'];
          setIsViewMode(nonEditableStatuses.includes(appData.status));
        } else {
          setIsViewMode(false);
        }
      } catch (err: any) {
        console.error('Error fetching application details:', err);
        setError(err.message || 'Failed to load application details. Please try again.');
        setIsViewMode(false); // Default to not view mode on error
      } finally {
        setIsLoading(false);
      }
    };

    getApplicationStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]); // Rerun if location.state changes, which might carry a new applicationId

  // Clean up the viewMode flag when component unmounts
  useEffect(() => {
    return () => {
      // Clean up session storage on unmount
      sessionStorage.removeItem('viewMode');
    };
  }, []);

  // TEMPORARY: Early return to display Page 1 for testing
    if (isLoading) {
    return (
      <Container>
        <Card style={{ textAlign: 'center' }}>
          <Title>Loading Application...</Title>
          <p>Please wait while we fetch the application details.</p>
          {/* You can add a spinner component here */}
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Card style={{ textAlign: 'center' }}>
          <Title>Error</Title>
          <Subtitle style={{color: 'red'}}>{error}</Subtitle>
          {/* Optionally, a button to go back or retry */}
        </Card>
      </Container>
    );
  }

  // If no applicationId is resolved here and it's required for ApplicationFormPage1 to function,
  // you might want to redirect or show a specific message before rendering ApplicationFormPage1.
  // ApplicationFormPage1 itself has logic to derive applicationId from location.state or sessionStorage.

  return <ApplicationFormPage1 isViewMode={isViewMode} />;

  // Original content of ApplicationForm is removed to temporarily display ApplicationFormPage1 for testing.
  // All hooks, state, logic, and JSX from the original form were here.
};

export default ApplicationForm;
